// Pieza del descuento de accesorios (slot `accessoryDiscount`): al entrar el primer accesorio que
// califica, aparta la promo en la ancla sin `lineIds` junto a la del teléfono; sin accesorios, la suelta.
import { useEffect, useRef, useState } from 'react';
import type { AnchorSlotContext } from '@/domains/sales/wizard/steps/BuildSaleStep';
import { useSalePromotionEvaluation, useStoreSalePromotions } from '../api/useWizardPromotions';
import { useAnchorSaleContext } from '../hooks/useAnchorSaleContext';
import { useApplyPromotions } from '../hooks/useApplyPromotions';
import {
    accessoryDiscountCandidate,
    accessoryOnlyIds,
    heldAccessoryDiscounts,
    withoutPromotions,
} from '../lib/accessoryDiscount';
import { promotionErrorCopy } from '../lib/promotionErrorCopy';
import { rememberedPromotionName } from '../lib/promotionNames';
import { withStackable } from '../lib/promotionSelection';
import { useSessionPromotionState } from '../store/promotionSelectionStore';

export interface AccessoryDiscountSyncProps {
    sessionId: string;
    context: AnchorSlotContext;
}

export function AccessoryDiscountSync({ sessionId, context }: AccessoryDiscountSyncProps) {
    const { anchorLine, quoteState } = context;
    const extraLines = context.extraLines ?? [];
    const { synced, selection } = useSessionPromotionState(sessionId);
    const evaluation = useSalePromotionEvaluation(sessionId);
    const { data: store } = useStoreSalePromotions(sessionId);
    const { withoutSureKeep } = useAnchorSaleContext(sessionId, anchorLine);
    const { apply, isApplying } = useApplyPromotions({ sessionId, context });
    const [rejection, setRejection] = useState<{ key: string; message: string }>();
    const tried = useRef(new Set<string>());

    const storeItems = store?.items ?? [];
    const data = evaluation.isPlaceholderData ? undefined : evaluation.data;
    const anchorId = anchorLine.lineId;
    const held = data ? heldAccessoryDiscounts(selection, accessoryOnlyIds(data, storeItems)) : [];
    const candidate = data
        ? accessoryDiscountCandidate(data, storeItems, extraLines, withoutSureKeep)
        : undefined;
    const key = candidate ? `${anchorId}|${candidate.promotionId}` : '';
    const ready = synced && data !== undefined && !isApplying && quoteState !== 'charged';

    // Sin accesorios que califiquen, se olvida lo intentado: el siguiente vuelve a apartar.
    useEffect(() => {
        if (candidate === undefined) tried.current.clear();
    }, [candidate]);

    useEffect(() => {
        if (!ready) return;
        if (candidate === undefined && held.length > 0) {
            const releasing = `release|${anchorId}|${held.join('|')}`;
            if (tried.current.has(releasing)) return;
            tried.current.add(releasing);
            apply((base) => withoutPromotions(base, held)).catch(() => undefined);
            return;
        }
        if (!candidate || held.length > 0 || tried.current.has(key)) return;
        tried.current.add(key);
        const choice = {
            promotionId: candidate.promotionId,
            promotionVersion: candidate.version,
            giftSkus: [],
        };
        // Va en la ancla sin ocupar el lugar de su «sola»: el servidor la cuenta en los accesorios.
        apply((base) => {
            const next = { ...base, anchorLineId: base.anchorLineId ?? anchorId };
            return withStackable(next, next.anchorLineId, choice, true);
        })
            .then(() => {
                setRejection(undefined);
            })
            .catch((error: unknown) => {
                setRejection({
                    key,
                    message: promotionErrorCopy(error, (id) =>
                        rememberedPromotionName(sessionId, id),
                    ),
                });
            });
    });

    const message = rejection?.key === key && held.length === 0 ? rejection.message : undefined;
    if (!message) return null;
    return (
        <p role="status" className="text-[12px] text-warning">
            {candidate ? `${candidate.name}: ${message}` : message}
        </p>
    );
}
